import { API_BASE_URL } from "../constants/api";
import {
  IFRAME_HEIGHT_OFFSET,
  IFRAME_STYLE_TAG_ID,
  darkModeStyles,
} from "../constants/privacyPolicyConstants";
import { UtilsLocale } from "./UtilsLocale";

/**
 * Type for the privacy policy tab
 */
export type PrivacyPolicyTabType = "global" | "local";

/**
 * Interface for tab items displayed in the privacy policy tabs
 */
export interface TabItem {
  name: string;
}

/**
 * Build the privacy policy source URL based on parameters
 * @param countryCode - The country code (e.g., "US", "CZ")
 * @param folderType - The folder type ("global" or "local" for EU countries)
 * @param langCode - The language code (e.g., "en", "ko")
 * @param dateSource - The date source string (e.g., "2024-01-01")
 * @param ppExist - Whether privacy policy exists for this language
 * @param hasLPP - Whether the country has an LPP
 * @returns The complete URL to the privacy policy HTML file
 */
export const buildPPSourcePath = (
  countryCode: string,
  folderType: PrivacyPolicyTabType,
  langCode: string,
  dateSource: string,
  ppExist: boolean,
  hasLPP: boolean = false
): string => {
  if (ppExist) {
    // Non-EU countries loads the GPP under global/* path
    return hasLPP
      ? `${API_BASE_URL}/privacy_policy/${countryCode}/${folderType}/${langCode}/${dateSource}.html`
      : `${API_BASE_URL}/privacy_policy/${countryCode}/global/${langCode}/${dateSource}.html`;
  }
  return `${API_BASE_URL}/privacy_policy/ZZ/${folderType}/en/${dateSource}.html`;
};

/**
 * Build the version history JSON URL based on parameters
 * @param countryCode - The country code (e.g., "US", "CZ")
 * @param folderType - The folder type ("global" or "local" for EU countries, undefined for non-EU)
 * @param langCode - The language code (e.g., "en", "ko")
 * @param hasLPP - Whether the country has LPP
 * @returns The complete URL to the version_history.json file
 */
export const buildVersionHistoryPath = (
  countryCode: string,
  folderType: PrivacyPolicyTabType,
  langCode: string,
  hasLPP: boolean = false
): string => {
  // Non-LPP countries don't have the folderType segment in the URL
  const path = hasLPP
    ? `${API_BASE_URL}/privacy_policy/${countryCode}/${folderType}/${langCode}/version_history.json`
    : `${API_BASE_URL}/privacy_policy/${countryCode}/global/${langCode}/version_history.json`;

  return path;
};

/**
 * Determine the folder type based on EU status and tab type
 * @param hasLPP - Whether the country has LPP
 * @param tabType - The current tab type selection
 * @returns The folder type to use for URL building
 */
export const determineFolderType = (
  hasLPP: boolean,
  tabType?: PrivacyPolicyTabType
): PrivacyPolicyTabType => {
  // EU countries default to 'global' tab, non-EU countries use 'global'
  if (hasLPP) {
    return tabType || "global";
  }
  return "global";
};

/**
 * Apply dark mode theme to iframe document
 * @param doc - The iframe document object
 * @param isDarkMode - Whether dark mode is enabled
 */
export const applyIframeTheme = (doc: Document, isDarkMode: boolean): void => {
  const html = doc.getElementsByTagName("html")[0];

  // Apply theme attribute
  if (isDarkMode) {
    html.dataset.theme = "dark";
  } else {
    delete html.dataset.theme;
  }

  // Inject CSS for dark mode
  if (!doc.getElementById(IFRAME_STYLE_TAG_ID)) {
    const style = doc.createElement("style");
    style.id = IFRAME_STYLE_TAG_ID;
    style.innerHTML = darkModeStyles;
    doc.head.appendChild(style);
  }
};

/**
 * Set iframe height based on content
 * @param doc - The iframe document object
 * @param iframe - The iframe element
 */
export const setIframeHeight = (
  doc: Document,
  iframe: HTMLIFrameElement
): void => {
  iframe.height = doc.body.scrollHeight + IFRAME_HEIGHT_OFFSET + "px";
};

/**
 * Check if privacy policy exists for a given language
 * @param language - The language string in format "langCode-countryCode" (e.g., "en-US")
 * @returns Whether privacy policy exists for this language
 */
export const hasPrivacyPolicyForLanguage = (language: string): boolean => {
  return UtilsLocale.hasPrivacyPolicy(language);
};

/**
 * Parse language string into components
 * @param language - The language string in format "langCode-countryCode" (e.g., "en-US")
 * @returns Tuple of [langCode, countryCode]. Returns ["", ""] for malformed input.
 */
export const parseLanguageString = (language: string): [string, string] => {
  const parts = language.split("-");
  if (parts.length !== 2) {
    return ["", ""];
  }
  return [parts[0], parts[1]];
};
